import { useCallback, useMemo } from 'react';
import uFuzzy from '@leeoniya/ufuzzy';
import { SupportedLocale } from '@99designs/i18n';
import { getDesignProductPresenters } from '../products/productsPresenter';
import { DesignProductSearchResult, RangeTuple } from '../types/Search';
import { useSearch } from './useSearch';
import type { DesignProductPresenter } from '../products/types';

const SEARCH_DELAY = 1000;
const DEBOUNCE_DELAY = 450;
export const MIN_SEARCH_QUERY_LENGTH = 3;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useDesignProductSearch = (locale: SupportedLocale) => {
    const products = useMemo(() => Object.values(getDesignProductPresenters(locale)), [locale]);

    const queryFn = useCallback(
        async (searchQuery: string) => {
            const results = uFuzzyToDesignProductSearchResults(searchQuery, products);
            // simulate network delay to increase perceived performance
            await delay(SEARCH_DELAY);
            return results;
        },
        [products]
    );

    const { search, results, loading, searchQuery } = useSearch<DesignProductSearchResult>({
        debounceDelay: DEBOUNCE_DELAY,
        loadingMode: 'immediate',
        searchFn: queryFn,
        minSearchQueryLength: MIN_SEARCH_QUERY_LENGTH,
    });

    return {
        search,
        results,
        loading,
        searchQuery,
    };
};

const uFuzzyToDesignProductSearchResults = (
    searchQuery: string,
    products: DesignProductPresenter[]
): DesignProductSearchResult[] => {
    // Setup allows 1 typo character which can include a - (Allows tshirt to match t-shirt)
    const uf = new uFuzzy({
        intraMode: 1,
        intraChars: '[a-z\\\\d\\-]',
        intraIns: 1,
    });

    const idxs = uf.filter(
        products.map((product) => uFuzzy.latinize(product.translatedTitle())),
        uFuzzy.latinize(searchQuery)
    );
    const info = uf.info(
        idxs || [],
        products.map((product) => uFuzzy.latinize(product.translatedTitle())),
        uFuzzy.latinize(searchQuery)
    );

    return (
        idxs?.map((idx, index) => {
            const indices: RangeTuple[] = info?.ranges[index]?.reduce(
                (acc, value, index, array) => {
                    if (index % 2 === 0) {
                        acc.push([value, array[index + 1]]);
                    }
                    return acc;
                },
                [] as RangeTuple[]
            );

            return {
                designProductPresenter: products[idx],
                matches: [
                    {
                        key: 'translatedTitle',
                        value: products[idx].translatedTitle(),
                        indices: indices,
                    },
                ],
            };
        }) || []
    );
};
